import {useCallback, useEffect, useRef} from 'react';
import {ehrService} from '../services/ehrService';

// Client-side throttle for the opportunistic same-day EHR roster
// refresh fired when the provider lands on / returns to the hub. Matches
// the backend's `/sync/me` throttle (USER_LOGIN_SYNC_INTERVAL_MS) so we
// don't burn round-trips that would just return `deduped: true`.
const HOME_EHR_SYNC_MIN_INTERVAL_MS = 15 * 60 * 1000;

// How often the background timer *checks* whether a refresh is due. This
// is intentionally much shorter than the throttle window: the check is a
// cheap timestamp comparison, and only fires an actual `/sync/me` once
// `HOME_EHR_SYNC_MIN_INTERVAL_MS` has elapsed since the last trigger
// (mount, window focus, or a prior tick). Polling at a sub-window cadence
// keeps the effective refresh interval close to 15 min for a clinician
// who sits on the hub for hours, without ever firing two syncs back to
// back.
const HOME_EHR_SYNC_POLL_MS = 60 * 1000;

// Module scope is deliberate: planner <-> archive navigation shares one
// throttle window instead of re-firing `/sync/me` on every surface switch.
const lastHomeSyncAtRef = {current: 0};

export function useSameDayEhrSync({
  userId,
  onNewAppointments,
  enabled = true,
}: Record<string, any> = {}) {
  const onNewAppointmentsRef = useRef(onNewAppointments);

  useEffect(() => {
    onNewAppointmentsRef.current = onNewAppointments;
  }, [onNewAppointments]);

  const handleSyncResult = useCallback((result: any) => {
    if (
      result?.status === 'completed' &&
      !result.deduped &&
      (result.appointments_new ?? 0) > 0
    ) {
      onNewAppointmentsRef.current?.();
    }
  }, []);

  const maybeRefreshEhrRoster = useCallback(async () => {
    if (!enabled || !userId) return;
    const now = Date.now();
    if (now - lastHomeSyncAtRef.current < HOME_EHR_SYNC_MIN_INTERVAL_MS) return;
    lastHomeSyncAtRef.current = now;
    try {
      const result = await ehrService.syncUserOnLogin();
      handleSyncResult(result);
    } catch {
      // Best-effort: the mount-time roster fetch already covers the
      // common case; a failed refresh just means no *extra* same-day
      // rows surfaced this visit.
    }
  }, [enabled, handleSyncResult, userId]);

  const refreshNow = useCallback(
    async ({notify = true} = {}) => {
      if (!enabled || !userId) return null;
      lastHomeSyncAtRef.current = Date.now();
      const result = await ehrService.syncUserOnLogin({force: true});
      if (notify) handleSyncResult(result);
      return result;
    },
    [enabled, handleSyncResult, userId],
  );

  useEffect(() => {
    maybeRefreshEhrRoster();
    const onFocus = () => {
      maybeRefreshEhrRoster();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefreshEhrRoster();
    };
    // Background timer so a clinician parked on the hub for hours still
    // picks up same-day additions even with no focus/visibility changes.
    // `maybeRefreshEhrRoster` self-throttles, so a tick within 15 min of
    // any other trigger is a no-op — this never double-fires. Skipped
    // while the tab is hidden so a backgrounded session doesn't churn the
    // network; the visibility/focus handlers cover the return-to-foreground.
    const pollId = setInterval(() => {
      if (document.visibilityState === 'visible') maybeRefreshEhrRoster();
    }, HOME_EHR_SYNC_POLL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(pollId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [maybeRefreshEhrRoster]);

  return {refreshNow};
}

export default useSameDayEhrSync;
